import React, { useState, useMemo, useRef, useCallback, memo, useEffect } from 'react';
import { SalesData, ForecastData } from '../types';
import { exportToCSV } from '../utils/exportUtils';
import { SalesChart } from './SalesChart';
import { DateRangePicker } from './DateRangePicker';
import { SearchFilter, FilterType } from './SearchFilter';
import { matchesFilterCriteria, formatNumberWithCommas, formatComparisonText, isDeadSku, isOutlierSku } from '../utils/forecastUtils';
import { ForecastGraph } from './ForecastGraph';

interface ForecastTabsProps {
  forecasts: ForecastData;
  salesData: SalesData[];
  defaultSalesData: SalesData[];
}

// Add a simple modal component for dead SKUs selection
const DeadSkusModal: React.FC<{
  open: boolean;
  onClose: () => void;
  deadSkus: string[];
  excludedSkus: Set<string>;
  onToggleSku: (sku: string) => void;
  onSelectAll: () => void;
  onRemoveAll: () => void;
  salesData: SalesData[];
}> = ({ open, onClose, deadSkus, excludedSkus, onToggleSku, onSelectAll, onRemoveAll, salesData }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxHeight: '90vh', overflow: 'auto', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' }}>
        <button
          className="absolute top-2 right-3 text-2xl btn-icon"
          onClick={onClose}
          aria-label="Close"
        >×</button>
        <h3>Manage Dead SKUs</h3>
        <div className="modal-actions">
          <button 
            onClick={onSelectAll}
            className="btn-primary"
          >
            Select All
          </button>
          <button 
            onClick={onRemoveAll}
            className="btn-secondary"
          >
            Remove All
          </button>
          <button 
            onClick={onClose} 
            className="btn-secondary"
          >
            Cancel
          </button>
        </div>
        <div className="modal-list">
          {deadSkus.map(sku => {
            const product = salesData.find(item => item.sku === sku);
            return (
              <label key={sku} className="modal-list-item">
                <input
                  type="checkbox"
                  checked={excludedSkus.has(sku)}
                  onChange={() => onToggleSku(sku)}
                />
                <span>{sku} {product ? `- ${product.productName}` : ''}</span>
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button 
            onClick={onClose} 
            className="btn-secondary"
          >
            Cancel
          </button>
          <button 
            onClick={onClose} 
            className="btn-primary"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// Outlier Excludes Modal/Dropdown
const OutlierExcludesModal: React.FC<{
  open: boolean;
  onClose: () => void;
  outlierSkus: string[];
  excludedOutlierSkus: Set<string>;
  onToggleSku: (sku: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  salesData: SalesData[];
}> = ({ open, onClose, outlierSkus, excludedOutlierSkus, onToggleSku, onSelectAll, onSelectNone, salesData }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxHeight: '90vh', overflow: 'auto', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' }}>
        <button className="modal-close-x" onClick={onClose} aria-label="Close">×</button>
        <div className="outlier-dropdown-header">
          <span className="outlier-dropdown-title">Manage Outlier Excludes</span>
          <div className="outlier-dropdown-actions">
            <button 
              onClick={onSelectAll}
              className="btn-primary"
            >
              Select All
            </button>
            <button 
              onClick={onSelectNone}
              className="btn-secondary"
            >
              Select None
            </button>
          </div>
        </div>
        <div className="outlier-dropdown-list">
          {outlierSkus.map(sku => {
            const product = salesData.find(item => item.sku === sku);
            return (
              <label key={sku} className="outlier-dropdown-item">
                <input
                  type="checkbox"
                  checked={excludedOutlierSkus.has(sku)}
                  onChange={() => onToggleSku(sku)}
                />
                <span>{sku} {product ? `- ${product.productName}` : ''}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Enhanced InfoTooltip for YOY with math breakdown
const InfoTooltip: React.FC<{ text: string; breakdown?: string; breakdownSections?: { formula?: string, details?: string, yoy?: string } }> = ({ text, breakdown, breakdownSections }) => {
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  return (
    <span className="info-tooltip-wrapper">
      <span 
        className="info-icon" 
        tabIndex={0} 
        style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          position: 'relative', 
          cursor: 'pointer',
          fontSize: '12px',
          color: '#666',
          marginLeft: '4px'
        }}
        onClick={() => setInfoModalOpen(true)}
      >
        ⓘ
      </span>
      {infoModalOpen && (
        <div className="modal-overlay" onClick={() => setInfoModalOpen(false)}>
          <div className="modal-content tooltip-modal" onClick={e => e.stopPropagation()}>
            <button
              className="modal-close-x"
              onClick={() => setInfoModalOpen(false)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 8,
                right: 12,
                fontSize: 24,
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >×</button>
            {text && (
              <div className="section">
                <strong>YOY Formula:</strong><br />
                <code>(Forecast − Historical Avg) / Historical Avg</code><br />
                Shows percent and unit difference from the historical average for this month.
              </div>
            )}
            {breakdownSections ? (
              <>
                <div className="section">
                  <strong>{breakdownSections.details || ''}</strong><br />
                  {breakdownSections.details}
                </div>
                <div className="section">
                  <strong>YOY:</strong> <code>{breakdownSections.formula}</code> → <strong>{breakdownSections.yoy}</strong>
                </div>
              </>
            ) : breakdown ? (
              <div style={{ marginTop: 8, fontSize: '0.97em', color: '#333', whiteSpace: 'pre-line' }}>{breakdown}</div>
            ) : null}
          </div>
        </div>
      )}
    </span>
  );
};

// Add info modal for dead SKUs definition
const InfoModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxHeight: '90vh', overflow: 'auto', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' }}>
        <h3>What is a Dead SKU?</h3>
        <p>A dead sku is defined here as 0 sales in the past 60 days. Excluding dead skus will help provide more accurate data for predictions.</p>
        <button onClick={onClose} style={{ float: 'right' }}>Close</button>
      </div>
    </div>
  );
};

// SVG edit icon as a React component
// const EditIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="auto" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-edit" style={style}>
//     <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
//     <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
//   </svg>
// );

// Add new info modal components
const DeadSkusInfoModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxHeight: '90vh', overflow: 'auto', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' }}>
        <button
          className="modal-close-x"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 8,
            right: 12,
            fontSize: 24,
            background: 'none',
            border: 'none',
            cursor: 'pointer'
          }}
        >×</button>
        <h3>What are Dead SKUs?</h3>
        <p>A dead SKU is defined as having no sales in the past 60 days. Excluding dead SKUs helps provide more accurate data for predictions by focusing on active products.</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={{ background: '#219150', color: 'white', fontWeight: 500, borderRadius: 4, padding: '6px 18px' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

const OutlierInfoModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxHeight: '90vh', overflow: 'auto', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' }}>
        <button
          className="modal-close-x"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 8,
            right: 12,
            fontSize: 24,
            background: 'none',
            border: 'none',
            cursor: 'pointer'
          }}
        >×</button>
        <h3>What are Outlier Excludes?</h3>
        <p>Outlier excludes help in providing more accurate forecasts by removing data points that are significantly different from others. In the case of Shopify outliers, this includes digital products (such as shipping protection and "skip the line") and freebies This helps to prevent skewed predictions based on unusual sales patterns.</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={{ background: '#219150', color: 'white', fontWeight: 500, borderRadius: 4, padding: '6px 18px' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Memoize the SearchFilter component
const MemoizedSearchFilter = memo(SearchFilter);

// Memoize the DateRangePicker component
const MemoizedDateRangePicker = memo(DateRangePicker);

// Memoize the SalesChart component
const MemoizedSalesChart = memo(SalesChart);

export const ForecastTabs: React.FC<ForecastTabsProps> = ({ forecasts, salesData, defaultSalesData }) => {
  const [activeTab, setActiveTab] = useState<'forecast' | 'smoothed' | 'historical' | 'products'>('forecast');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('contains');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [excludeDeadSkus, setExcludeDeadSkus] = useState<boolean>(false);
  const [excludeOutliers, setExcludeOutliers] = useState<boolean>(false);
  const [showTotalsForAll, setShowTotalsForAll] = useState<boolean>(true);
  const [deadSkusInfoModalOpen, setDeadSkusInfoModalOpen] = useState(false);
  const [outlierInfoModalOpen, setOutlierInfoModalOpen] = useState(false);
  const [deadSkusModalOpen, setDeadSkusModalOpen] = useState(false);
  const [outlierModalOpen, setOutlierModalOpen] = useState(false);
  const [excludedOutlierSkus, setExcludedOutlierSkus] = useState<Set<string>>(new Set());
  const forecastSectionRef = useRef<HTMLDivElement>(null);
  const [productViewType, setProductViewType] = useState<'cards' | 'table'>('cards');
  const [viewType, setViewType] = useState<'table' | 'graph'>('table');
  const [historicViewType, setHistoricViewType] = useState<'table' | 'graph'>('table');
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Track which dead SKUs are excluded
  const deadSkus = useMemo(() => {
    const skus = Array.from(new Set(salesData.map(item => item.sku)));
    return skus.filter(sku => isDeadSku(sku, salesData, 60));
  }, [salesData]);
  const [excludedDeadSkus, setExcludedDeadSkus] = useState<Set<string>>(new Set(deadSkus));

  // Track outlier SKUs
  const outlierSkus = useMemo(() => {
    return Array.from(new Set(salesData
      .filter(item => isOutlierSku(item.sku, item.productName))
      .map(item => item.sku)));
  }, [salesData]);

  // Only sync excludedDeadSkus with deadSkus on initial mount or when salesData changes
  useEffect(() => {
    setExcludedDeadSkus(new Set(deadSkus));
  }, [salesData, deadSkus]);

  // Outlier modal handlers
  const handleToggleOutlierSku = (sku: string) => {
    const newExcludedSkus = new Set(Array.from(excludedOutlierSkus));
    if (newExcludedSkus.has(sku)) {
      newExcludedSkus.delete(sku);
    } else {
      newExcludedSkus.add(sku);
    }
    setExcludedOutlierSkus(newExcludedSkus);
  };
  const handleSelectAllOutliers = () => {
    const allSkus = Array.from(outlierSkus);
    setExcludedOutlierSkus(new Set(allSkus));
  };
  const handleSelectNoneOutliers = () => setExcludedOutlierSkus(new Set());

  // Modal handlers
  const handleToggleSku = (sku: string) => {
    const newExcludedSkus = new Set(Array.from(excludedDeadSkus));
    if (newExcludedSkus.has(sku)) {
      newExcludedSkus.delete(sku);
    } else {
      newExcludedSkus.add(sku);
    }
    setExcludedDeadSkus(newExcludedSkus);
  };
  const handleSelectAll = () => {
    const allSkus = Array.from(deadSkus);
    setExcludedDeadSkus(new Set(allSkus));
  };
  const handleRemoveAll = () => setExcludedDeadSkus(new Set());

  // Memoize the matchesSearch function
  const matchesSearch = useCallback((sku: string, productName: string) => {
    return matchesFilterCriteria(sku, searchTerm, filterType) || 
           matchesFilterCriteria(productName, searchTerm, filterType);
  }, [searchTerm, filterType]);

  // Calculate historical monthly averages by SKU and month (for comparison)
  const historicalMonthlyAverages = useMemo(() => {
    const skuMonthData: { [key: string]: { [month: string]: { qty: number[], revenue: number[] } } } = {};

    // Single pass through salesData to build the data structure
    salesData.forEach(item => {
      if (item.salesQuantity <= 0 && item.salesRevenue <= 0) return;

      const sku = item.sku;
      const month = item.period.split('-')[1];
      
      if (!skuMonthData[sku]) {
        skuMonthData[sku] = {};
      }
      
      if (!skuMonthData[sku][month]) {
        skuMonthData[sku][month] = { qty: [], revenue: [] };
      }
      
      skuMonthData[sku][month].qty.push(item.salesQuantity);
      skuMonthData[sku][month].revenue.push(item.salesRevenue);
    });
    
    // Calculate averages in a single pass
    const result: { [key: string]: { [month: string]: { avgQty: number, avgRevenue: number } } } = {};
    
    Object.entries(skuMonthData).forEach(([sku, monthData]) => {
      result[sku] = {};
      
      Object.entries(monthData).forEach(([month, data]) => {
        if (data.qty.length > 0) {
          const sumQty = data.qty.reduce((sum, qty) => sum + qty, 0);
          const sumRev = data.revenue.reduce((sum, rev) => sum + rev, 0);
          const len = data.qty.length;
          
          result[sku][month] = {
            avgQty: sumQty / len,
            avgRevenue: sumRev / len
          };
        }
      });
    });
    
    return result;
  }, [salesData]);

  // Calculate monthly averages for each product
  const productMonthlyAverages = useMemo(() => {
    const result: { [key: string]: { avgQty: number, avgRevenue: number } } = {};
    
    // Group by SKU
    const skuGroups: { [sku: string]: SalesData[] } = {};
    salesData.forEach(item => {
      if (!skuGroups[item.sku]) {
        skuGroups[item.sku] = [];
      }
      skuGroups[item.sku].push(item);
    });
    
    // Calculate the unique months each product has data for
    Object.entries(skuGroups).forEach(([sku, data]) => {
      const uniquePeriods = new Set(data.map(item => item.period)).size;
      const totalQty = data.reduce((sum, item) => sum + item.salesQuantity, 0);
      const totalRevenue = data.reduce((sum, item) => sum + item.salesRevenue, 0);
      
      result[sku] = {
        avgQty: totalQty / uniquePeriods,
        avgRevenue: totalRevenue / uniquePeriods
      };
    });
    
    return result;
  }, [salesData]);

  // Memoize the filtered data calculations
  const filteredData = useMemo(() => {
    const filtered = salesData.filter(item => {
      if (!matchesSearch(item.sku, item.productName)) return false;
      
      // Ensure data is within the selected date range
      if (startDate || endDate) {
        const itemDate = new Date(item.period + "-01");
        if (startDate && itemDate < startDate) return false;
        if (endDate) {
          const endDatePlusMonth = new Date(endDate);
          endDatePlusMonth.setMonth(endDatePlusMonth.getMonth() + 1);
          if (itemDate >= endDatePlusMonth) return false;
        }
      }
      
      if (excludeDeadSkus && excludedDeadSkus.has(item.sku)) return false;
      if (excludeOutliers && (isOutlierSku(item.sku, item.productName) || excludedOutlierSkus.has(item.sku))) return false;
      
      return true;
    });

    return {
      historical: filtered,
      products: forecasts.byProduct.filter(product => {
        const searchMatches = matchesSearch(product.sku, product.productName);
        if (!searchMatches) return false;
        if (excludeDeadSkus && excludedDeadSkus.has(product.sku)) return false;
        if (excludeOutliers && (isOutlierSku(product.sku, product.productName) || excludedOutlierSkus.has(product.sku))) return false;
        return true;
      })
    };
  }, [salesData, forecasts.byProduct, startDate, endDate, excludeDeadSkus, excludedDeadSkus, excludeOutliers, excludedOutlierSkus, matchesSearch]);

  // Memoize the totals calculations
  const totals = useMemo(() => {
    const { products } = filteredData;
    
    const totalQuantity = products.reduce((sum, product) => sum + product.totalQuantity, 0);
    const totalRevenue = products.reduce((sum, product) => sum + product.totalRevenue, 0);
    
    const monthlySummary = forecasts.monthlySummary
      .filter(month => {
        if (!startDate && !endDate) return true;
        const monthDate = new Date(month.period + "-01");
        if (startDate && monthDate < startDate) return false;
        if (endDate) {
          const endDatePlusMonth = new Date(endDate);
          endDatePlusMonth.setMonth(endDatePlusMonth.getMonth() + 1);
          if (monthDate >= endDatePlusMonth) return false;
        }
        return true;
      })
      .map(month => {
      const totalQuantity = products.reduce(
        (sum, product) => sum + (product.forecasts.find(f => f.period === month.period)?.salesQuantity || 0),
        0
      );
      
      const totalRevenue = products.reduce(
        (sum, product) => sum + (product.forecasts.find(f => f.period === month.period)?.salesRevenue || 0),
        0
      );
      
      return { period: month.period, totalQuantity, totalRevenue };
    });
    
    return { totalQuantity, totalRevenue, monthlySummary };
  }, [filteredData, forecasts.monthlySummary, startDate, endDate]);

  // Memoize the tooltip calculations
  const getTooltipData = useCallback((month: any, sku: string) => {
    const monthNum = month.period.split('-')[1];
    const historicalDataPoints = salesData
      .filter(item => item.sku === sku && item.period.split('-')[1] === monthNum)
      .sort((a, b) => a.period.localeCompare(b.period));

    if (historicalDataPoints.length === 0) return null;

    const avg = historicalDataPoints.reduce((sum, item) => sum + item.salesQuantity, 0) / historicalDataPoints.length;
    const avgRev = historicalDataPoints.reduce((sum, item) => sum + item.salesRevenue, 0) / historicalDataPoints.length;
    
    const growthRate = forecasts.overallSummary.growthRate;
    const predicted = Math.round(avg * (1 + growthRate / 100));
    const predictedRev = Math.round(avgRev * (1 + growthRate / 100));
    
    return {
      historicalDataPoints,
      avg,
      avgRev,
      predicted,
      predictedRev,
      growthRate
    };
  }, [salesData, forecasts.overallSummary.growthRate]);

  // Update the renderInfoTooltip to use memoized data
  const renderInfoTooltip = useCallback((month: any, historicalAvg: number, historicalRev: number, diffPercent: number, revDiffPercent: number) => {
    const tooltipData = getTooltipData(month, month.sku);
    if (!tooltipData) return null;

    const { historicalDataPoints, avg, predicted, growthRate } = tooltipData;
    const unitsChange = predicted - avg;
    const percentChange = ((predicted - avg) / avg * 100).toFixed(2);

    // Get all historical data points for this month across all years
    const allHistoricalPoints = salesData
      .filter(item => item.sku === month.sku && item.period.split('-')[1] === month.period.split('-')[1])
      .sort((a, b) => a.period.localeCompare(b.period));

    const rawData = allHistoricalPoints
      .map(item => `${item.period}: ${Math.round(item.salesQuantity)} units`)
      .join('\n');

    const historicalValues = historicalDataPoints.map(point => typeof point === 'number' ? point : point.salesQuantity);
    
    const breakdownSections = {
      details: rawData,
      formula: `Step 1: Sum Historical Values (${historicalValues.map(val => Math.round(val)).join(' + ')})\nStep 2: Calculate Historical Average (${historicalValues.reduce((a, b) => a + b, 0)} / ${historicalValues.length} = ${avg.toFixed(2)} units)\nStep 3: Apply Growth Rate ${avg.toFixed(2)} * (1 + ${(growthRate * 100).toFixed(1)}%) = ${predicted.toFixed(2)} units\nStep 4: Calculate YOY Change (${predicted.toFixed(2)} - ${avg.toFixed(2)}) = ${unitsChange.toFixed(2)} units`,
      yoy: `${Math.abs(unitsChange).toFixed(2)} units ${unitsChange >= 0 ? 'increase' : 'decrease'} (${Math.abs(parseFloat(percentChange))}% ${parseFloat(percentChange) >= 0 ? 'up' : 'down'})`
    };

    return (
      <InfoTooltip 
        text="YOY Calculation"
        breakdownSections={breakdownSections}
      />
    );
  }, [salesData, getTooltipData]);

  // Update the renderRevenueInfoTooltip similarly
  const renderRevenueInfoTooltip = useCallback((month: any, historicalRev: number, revDiffPercent: number) => {
    const tooltipData = getTooltipData(month, month.sku);
    if (!tooltipData) return null;

    const { historicalDataPoints, avgRev, predictedRev, growthRate } = tooltipData;
    const revChange = predictedRev - avgRev;
    const percentChange = ((predictedRev - avgRev) / avgRev * 100).toFixed(2);

    // Get all historical data points for this month across all years
    const allHistoricalPoints = salesData
      .filter(item => item.sku === month.sku && item.period.split('-')[1] === month.period.split('-')[1])
      .sort((a, b) => a.period.localeCompare(b.period));

    const rawData = allHistoricalPoints
      .map(item => `${item.period}: $${Math.round(item.salesRevenue)}`)
      .join('\n');

    const allYearsAvg = allHistoricalPoints.length > 0
      ? allHistoricalPoints.reduce((sum, item) => sum + item.salesRevenue, 0) / allHistoricalPoints.length
      : 0;

    return (
      <InfoTooltip 
        text="Year-over-Year (YOY) Revenue Calculation Breakdown"
        breakdownSections={{
          formula: `(${predictedRev} - ${allYearsAvg.toFixed(2)}) / ${allYearsAvg.toFixed(2)} * 100 = (${predictedRev} - ${avgRev.toFixed(2)}) / ${avgRev.toFixed(2)} * 100`,
          details: `Step 1: Historical Revenue Data Points for ${month.period.split('-')[1]} Month\n${rawData}\n\n` +
                  `Step 2: Calculate Historical Average Revenue\n` +
                  `(${historicalDataPoints.map(item => Math.round(item.salesRevenue)).join(' + ')}) / ${historicalDataPoints.length} = $${avgRev.toFixed(2)}\n\n` +
                  `Step 3: Apply Growth Rate\n` +
                  `$${avgRev.toFixed(2)} * (1 + ${growthRate}%) = $${predictedRev}\n\n` +
                  `Step 4: Calculate YOY Change\n` +
                  `Revenue Change: $${predictedRev} - $${avgRev.toFixed(2)} = $${revChange.toFixed(2)}\n` +
                  `Percent Change: ($${predictedRev} - $${avgRev.toFixed(2)}) / $${avgRev.toFixed(2)} * 100 = ${percentChange}%`,
          yoy: `${percentChange}% ($${revChange.toFixed(2)})`
        }}
      />
    );
  }, [getTooltipData]);

  // Memoize the handleForecastNow function
  const handleForecastNow = useCallback(() => {
    if (forecastSectionRef.current) {
      forecastSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Memoize the handleExport function
  const handleExport = useCallback(() => {
    setIsLoading(true);
    const exportData = filteredData.products.map(product => ({
      SKU: product.sku,
      'Product Name': product.productName,
      ...product.forecasts.reduce((acc, month) => ({
        ...acc,
        [`${month.period} Units`]: Math.round(month.salesQuantity),
        [`${month.period} Revenue`]: Math.round(month.salesRevenue)
      }), {}),
      'Total Units': Math.round(product.totalQuantity),
      'Total Revenue': Math.round(product.totalRevenue)
    }));

    exportToCSV(exportData, 'forecast_export.csv');
    setIsLoading(false);
  }, [filteredData.products]);

  // Add new function for exponential smoothing calculation
  const calculateSmoothedForecast = useCallback((sku: string, month: string, alpha: number = 0.3) => {
    // Get all historical data points for this SKU and month, sorted by period
    const historicalData = salesData
      .filter(item => item.sku === sku && item.period.split('-')[1] === month.split('-')[1])
      .sort((a, b) => a.period.localeCompare(b.period));

    if (historicalData.length === 0) return 0;

    // Initialize with first value
    let smoothedValue = historicalData[0].salesQuantity;
    
    // Apply exponential smoothing
    for (let i = 1; i < historicalData.length; i++) {
      smoothedValue = alpha * historicalData[i].salesQuantity + (1 - alpha) * smoothedValue;
    }

    return smoothedValue;
  }, [salesData]);

  // Calculate smoothed forecasts for all products
  const smoothedForecasts = useMemo(() => {
    return forecasts.byProduct.map(product => {
      const smoothedProductForecasts = product.forecasts.map(month => {
        // Calculate smoothed quantity using exponential smoothing
        const smoothedQty = calculateSmoothedForecast(product.sku, month.period);
        
        // Calculate average price per unit for revenue
        const skuSales = salesData.filter(item => item.sku === product.sku);
        const totalRevenue = skuSales.reduce((sum, item) => sum + item.salesRevenue, 0);
        const totalQuantity = skuSales.reduce((sum, item) => sum + item.salesQuantity, 0);
        const avgPrice = totalQuantity > 0 ? totalRevenue / totalQuantity : 0;

      return {
        ...month,
          salesQuantity: smoothedQty,
          salesRevenue: smoothedQty * avgPrice
      };
    });

      const totalQuantity = smoothedProductForecasts.reduce((sum, f) => sum + f.salesQuantity, 0);
      const totalRevenue = smoothedProductForecasts.reduce((sum, f) => sum + f.salesRevenue, 0);

    return {
      ...product,
        forecasts: smoothedProductForecasts,
        totalQuantity,
        totalRevenue
    };
  });
  }, [forecasts.byProduct, calculateSmoothedForecast, salesData]);

  // Add function to get all unique periods
  const allPeriods = useMemo(() => {
    return Array.from(new Set(salesData.map(item => item.period))).sort();
  }, [salesData]);

  // Add function to get sales data by SKU and period
  const getSalesDataBySkuPeriod = useCallback((sku: string) => {
    const skuData = salesData.filter(item => item.sku === sku);
    const periodData: { [period: string]: { qty: number, revenue: number } } = {};
    
    allPeriods.forEach(period => {
      const periodItems = skuData.filter(item => item.period === period);
      periodData[period] = {
        qty: periodItems.reduce((sum, item) => sum + item.salesQuantity, 0),
        revenue: periodItems.reduce((sum, item) => sum + item.salesRevenue, 0)
      };
    });
    
    return periodData;
  }, [salesData, allPeriods]);

  // Helper to get year from period string
  const getYear = (period: string) => period.split('-')[0];

  // Helper to get all years for a SKU
  const getYearsForSku = (sku: string) => {
    return Array.from(new Set(salesData.filter(item => item.sku === sku).map(item => getYear(item.period)))).sort();
  };

  // Helper to get total units for a SKU in a year
  const getTotalUnitsForSkuYear = (sku: string, year: string) => {
    return salesData.filter(item => item.sku === sku && getYear(item.period) === year)
      .reduce((sum, item) => sum + item.salesQuantity, 0);
  };

  // Helper to get date range for a SKU
  const getDateRangeForSku = (sku: string) => {
    const skuItems = salesData.filter(item => item.sku === sku);
    if (skuItems.length === 0) return { min: null, max: null };
    const dates = skuItems.map(item => new Date(item.period + '-01').getTime());
    return { min: new Date(Math.min(...dates)), max: new Date(Math.max(...dates)) };
  };

  // Combine historical and forecast data for the main graph
  const combinedGraphData = useMemo(() => {
    // 1. Get filtered historical data, grouped by period
    const periodMap: { [period: string]: { totalQuantity: number; totalRevenue: number } } = {};
    filteredData.historical.forEach(item => {
      const itemDate = new Date(item.period + '-01');
      if (startDate && itemDate < startDate) return;
      if (endDate) {
        const endDatePlusMonth = new Date(endDate);
        endDatePlusMonth.setMonth(endDatePlusMonth.getMonth() + 1);
        if (itemDate >= endDatePlusMonth) return;
      }
      if (!periodMap[item.period]) {
        periodMap[item.period] = { totalQuantity: 0, totalRevenue: 0 };
      }
      periodMap[item.period].totalQuantity += item.salesQuantity;
      periodMap[item.period].totalRevenue += item.salesRevenue;
    });
    const historical = Object.entries(periodMap).map(([period, vals]) => ({
      period,
      totalQuantity: vals.totalQuantity,
      totalRevenue: vals.totalRevenue,
      type: 'historical'
    }));

    // 2. Get filtered forecast data
    const forecast = totals.monthlySummary.map(month => {
      const monthDate = new Date(month.period + '-01');
      if (startDate && monthDate < startDate) return null;
      if (endDate) {
        const endDatePlusMonth = new Date(endDate);
        endDatePlusMonth.setMonth(endDatePlusMonth.getMonth() + 1);
        if (monthDate >= endDatePlusMonth) return null;
      }
      return {
        period: month.period,
        totalQuantity: month.totalQuantity,
        totalRevenue: month.totalRevenue,
        type: 'forecast'
      };
    }).filter((item): item is { period: string; totalQuantity: number; totalRevenue: number; type: string; } => item !== null);

    // 3. Combine and sort by period
    const all = [...historical, ...forecast];
    all.sort((a, b) => a.period.localeCompare(b.period));
    // Remove duplicates (prefer forecast if period overlaps)
    const unique: { [period: string]: typeof all[0] } = {};
    all.forEach(item => {
      unique[item.period] = item;
    });
    return Object.values(unique);
  }, [filteredData.historical, totals.monthlySummary, startDate, endDate]);

  return (
    <div className="forecast-tabs">
      <InfoModal open={infoModalOpen} onClose={() => setInfoModalOpen(false)} />
      <DeadSkusInfoModal open={deadSkusInfoModalOpen} onClose={() => setDeadSkusInfoModalOpen(false)} />
      <OutlierInfoModal open={outlierInfoModalOpen} onClose={() => setOutlierInfoModalOpen(false)} />
      <OutlierExcludesModal
        open={outlierModalOpen}
        onClose={() => setOutlierModalOpen(false)}
        outlierSkus={outlierSkus}
        excludedOutlierSkus={excludedOutlierSkus}
        onToggleSku={handleToggleOutlierSku}
        onSelectAll={handleSelectAllOutliers}
        onSelectNone={handleSelectNoneOutliers}
        salesData={salesData}
      />
      <DeadSkusModal
        open={deadSkusModalOpen}
        onClose={() => setDeadSkusModalOpen(false)}
        deadSkus={deadSkus}
        excludedSkus={excludedDeadSkus}
        onToggleSku={handleToggleSku}
        onSelectAll={handleSelectAll}
        onRemoveAll={handleRemoveAll}
        salesData={salesData}
      />
      <div className="tab-headers flex border-b border-gray-200">
        <button 
          className={`px-6 py-3 text-sm font-medium transition-all duration-200 relative ${
            activeTab === 'forecast' 
              ? 'text-[#00bed4] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#00bed4]' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveTab('forecast')}
        >
          Forecast by Growth Rate
        </button>
        <button 
          className={`px-6 py-3 text-sm font-medium transition-all duration-200 relative ${
            activeTab === 'smoothed' 
              ? 'text-[#00bed4] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#00bed4]' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveTab('smoothed')}
        >
          Smoothed
        </button>
        <button 
          className={`px-6 py-3 text-sm font-medium transition-all duration-200 relative ${
            activeTab === 'historical' 
              ? 'text-[#00bed4] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#00bed4]' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveTab('historical')}
        >
          Historical Sales
        </button>
        <button 
          className={`px-6 py-3 text-sm font-medium transition-all duration-200 relative ${
            activeTab === 'products' 
              ? 'text-[#00bed4] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#00bed4]' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => setActiveTab('products')}
        >
          Product Sales Trends
        </button>
      </div>

      <div className="tab-content">
        <div className="tab-controls" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, width: '100%' }}>
            <MemoizedSearchFilter 
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filterType={filterType}
              onFilterTypeChange={setFilterType}
              placeholder="Search by SKU or product name (comma-separated for multiple)"
            />
            <div style={{ marginLeft: 'auto', marginBottom: 16 }}>
              <MemoizedDateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
              />
            </div>
          </div>
          <div className="filter-options" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
            <label className="filter-option" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', borderRadius: 6 }}>
              <input
                type="checkbox"
                checked={excludeOutliers}
                onChange={() => setExcludeOutliers(v => !v)}
              />
              <span
                onClick={() => setOutlierModalOpen(true)}
                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
              >Outlier Excludes</span>
              <span 
                className="info-icon" 
                onClick={() => setOutlierInfoModalOpen(true)}
                style={{ cursor: 'pointer' }}
              >
                ⓘ
              </span>
              <button 
                className="btn-icon"
                aria-label="Edit" 
                onClick={() => setOutlierModalOpen(true)}
              >
                <span className="text-xs text-gray-600">✎</span>
              </button>
            </label>
            <label className="filter-option" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', borderRadius: 6 }}>
              <input
                type="checkbox"
                checked={excludeDeadSkus}
                onChange={() => setExcludeDeadSkus(v => !v)}
              />
              <span>Exclude Dead SKUs</span>
              <span 
                className="info-icon" 
                onClick={() => setDeadSkusInfoModalOpen(true)}
                style={{ cursor: 'pointer' }}
              >
                ⓘ
              </span>
              <button 
                className="btn-icon"
                aria-label="Edit" 
                onClick={() => setDeadSkusModalOpen(true)}
              >
                <span className="text-xs text-gray-600">✎</span>
              </button>
            </label>
            <label className="filter-option" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', borderRadius: 6 }}>
              <input
                type="checkbox"
                checked={showTotalsForAll}
                onChange={() => setShowTotalsForAll(v => !v)}
              />
              <span>Show totals for all SKUs</span>
            </label>
          </div>
          
          {(activeTab === 'forecast' || activeTab === 'smoothed') && (
            <div className="view-toggle" style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
              <button
                className={`px-4 py-2 text-sm rounded border ${
                  viewType === 'table' 
                    ? 'bg-[#006d79] text-white' 
                    : 'border-[#006d79] text-[#006d79]'
                }`}
                onClick={() => setViewType('table')}
              >
                Table View
              </button>
              <button
                className={`px-4 py-2 text-sm rounded border ${
                  viewType === 'graph' 
                    ? 'bg-[#006d79] text-white' 
                    : 'border-[#006d79] text-[#006d79]'
                }`}
                onClick={() => setViewType('graph')}
              >
                Graph View
              </button>
            </div>
          )}
          
          <button 
            className="btn-primary flex items-center gap-2"
            onClick={handleExport}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export to CSV
          </button>
        </div>

        {/* Graph summary for all tabs */}
        <div className="graph-summary" style={{ margin: '16px 0' }}>
          <div className="summary-cards">
            <div className="summary-card">
              <h4>Total Products</h4>
              <span>{filteredData.products.length}</span>
            </div>
            <div className="summary-card">
              <h4>Total Quantity</h4>
              <span>{formatNumberWithCommas(Math.round(totals.totalQuantity), 0)}</span>
            </div>
            <div className="summary-card">
              <h4>Total Revenue</h4>
              <span>${formatNumberWithCommas(Math.round(totals.totalRevenue), 2)}</span>
            </div>
          </div>
        </div>

        {activeTab === 'forecast' && (
          <div ref={forecastSectionRef} className="forecast-view">
            <div className="forecast-summary">
              <div className="summary-heading">
                <h3>Forecast Summary</h3>
                <div className="summary-stats">
                  <div className="summary-stat">
                    <span className="stat-label">Products:</span>
                    <span className="stat-value">{filteredData.products.length}</span>
                    {showTotalsForAll && (
                      <span className="stat-all">of {forecasts.byProduct.length}</span>
                    )}
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Units:</span>
                    <span className="stat-value">{formatNumberWithCommas(Math.round(totals.totalQuantity), 0)}</span>
                    {showTotalsForAll && (
                      <span className="stat-all">of {formatNumberWithCommas(Math.round(forecasts.overallSummary.totalQuantity), 0)}</span>
                    )}
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Revenue:</span>
                    <span className="stat-value">${formatNumberWithCommas(Math.round(totals.totalRevenue), 2)}</span>
                    {showTotalsForAll && (
                      <span className="stat-all">of ${formatNumberWithCommas(Math.round(forecasts.overallSummary.totalRevenue), 2)}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {viewType === 'table' ? (
            <div className="table-container">
              <table className="forecast-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product Name</th>
                    {forecasts.monthlySummary.map(month => [
                      <th key={month.period + '-qty'}>{month.period} Units</th>,
                      <th key={month.period + '-rev'}>{month.period} Revenue</th>
                    ])}
                    <th>Total Units</th>
                    <th>Total Revenue</th>
                    <th>Monthly Avg Units</th>
                    <th>Monthly Avg Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.products.map(product => (
                    <tr key={product.sku} className={deadSkus.includes(product.sku) ? 'dead-sku-row' : ''}>
                      <td>{product.sku}</td>
                      <td>{product.productName}</td>
                      {product.forecasts.map(month => {
                        const monthNum = month.period.split('-')[1];
                        // Get all historical data points for this month across all years
                        const allHistoricalPoints = salesData
                          .filter(item => item.sku === product.sku && item.period.split('-')[1] === monthNum)
                          .sort((a, b) => a.period.localeCompare(b.period));

                        const allYearsAvg = allHistoricalPoints.length > 0
                          ? allHistoricalPoints.reduce((sum, item) => sum + item.salesQuantity, 0) / allHistoricalPoints.length
                          : 0;

                        const allYearsRevAvg = allHistoricalPoints.length > 0
                          ? allHistoricalPoints.reduce((sum, item) => sum + item.salesRevenue, 0) / allHistoricalPoints.length
                          : 0;

                        const hasHistoricalData = allHistoricalPoints.length > 0;
                        const diff = hasHistoricalData ? month.salesQuantity - allYearsAvg : 0;
                        const diffPercent = hasHistoricalData && allYearsAvg > 0 ? ((month.salesQuantity - allYearsAvg) / allYearsAvg * 100) : 0;
                        const revDiff = hasHistoricalData ? month.salesRevenue - allYearsRevAvg : 0;
                        const revDiffPercent = hasHistoricalData && allYearsRevAvg > 0 ? ((month.salesRevenue - allYearsRevAvg) / allYearsRevAvg * 100) : 0;

                        return [
                          <td key={month.period + '-qty'} className="forecast-cell">
                            <div className="forecast-value">{formatNumberWithCommas(Math.round(month.salesQuantity), 0)}</div>
                            {hasHistoricalData && (
                              <div className="historical-comparison comparison-light">
                                {formatComparisonText(diff, diffPercent)}
                                {renderInfoTooltip({ ...month, sku: product.sku }, allYearsAvg, allYearsRevAvg, diffPercent, revDiffPercent)}
                              </div>
                            )}
                          </td>,
                          <td key={month.period + '-rev'} className="forecast-cell">
                            <div className="forecast-value">${formatNumberWithCommas(Math.round(month.salesRevenue), 2)}</div>
                            {hasHistoricalData && (
                              <div className="historical-comparison comparison-light">
                                {formatComparisonText(revDiff, revDiffPercent, false)}
                                {renderRevenueInfoTooltip({ ...month, sku: product.sku }, allYearsRevAvg, revDiffPercent)}
                              </div>
                            )}
                          </td>
                        ];
                      })}
                      <td>{formatNumberWithCommas(Math.round(product.totalQuantity), 0)}</td>
                      <td>${formatNumberWithCommas(Math.round(product.totalRevenue), 2)}</td>
                      <td>
                        {productMonthlyAverages[product.sku] 
                          ? formatNumberWithCommas(Math.round(productMonthlyAverages[product.sku].avgQty), 0)
                          : 'N/A'}
                      </td>
                      <td>
                        ${productMonthlyAverages[product.sku] 
                          ? formatNumberWithCommas(Math.round(productMonthlyAverages[product.sku].avgRevenue), 2)
                          : 'N/A'}
                      </td>
                    </tr>
                  ))}
                  <tr className="totals-row filtered-totals">
                    <td colSpan={2}>Filtered Total</td>
                    {totals.monthlySummary.map(month => (
                      <td key={month.period}>{formatNumberWithCommas(Math.round(month.totalQuantity), 0)}</td>
                    ))}
                    <td>{formatNumberWithCommas(Math.round(totals.totalQuantity), 0)}</td>
                    <td>${formatNumberWithCommas(Math.round(totals.totalRevenue), 2)}</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                  {showTotalsForAll && (
                    <tr className="totals-row all-totals">
                      <td colSpan={2}>All SKUs Total</td>
                      {forecasts.monthlySummary.map(month => (
                        <td key={month.period}>{formatNumberWithCommas(Math.round(month.totalQuantity), 0)}</td>
                      ))}
                      <td>{formatNumberWithCommas(Math.round(forecasts.overallSummary.totalQuantity), 0)}</td>
                      <td>${formatNumberWithCommas(Math.round(forecasts.overallSummary.totalRevenue), 2)}</td>
                      <td>-</td>
                      <td>-</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            ) : (
              <ForecastGraph
                data={combinedGraphData}
                title="Forecast Overview (Historical + Forecast)"
              />
            )}
          </div>
        )}

        {activeTab === 'smoothed' && (
          <div className="forecast-view">
            <div className="forecast-summary">
              <div className="summary-heading">
                <h3>Smoothed Forecast Summary</h3>
                <div className="summary-stats">
                  <div className="summary-stat">
                    <span className="stat-label">Products:</span>
                    <span className="stat-value">{filteredData.products.length}</span>
                    {showTotalsForAll && (
                      <span className="stat-all">of {forecasts.byProduct.length}</span>
                    )}
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Units:</span>
                    <span className="stat-value">{formatNumberWithCommas(Math.round(smoothedForecasts.reduce((sum, p) => sum + p.totalQuantity, 0)), 0)}</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Revenue:</span>
                    <span className="stat-value">${formatNumberWithCommas(Math.round(smoothedForecasts.reduce((sum, p) => sum + p.totalRevenue, 0)), 2)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {viewType === 'table' ? (
            <div className="table-container">
              <table className="forecast-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product Name</th>
                    {forecasts.monthlySummary.map(month => [
                      <th key={month.period + '-qty'}>{month.period} Units</th>,
                      <th key={month.period + '-rev'}>{month.period} Revenue</th>
                    ])}
                    <th>Total Units</th>
                    <th>Total Revenue</th>
                    <th>Monthly Avg Units</th>
                    <th>Monthly Avg Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.products.map(product => {
                    const smoothedProduct = smoothedForecasts.find(p => p.sku === product.sku);
                    if (!smoothedProduct) return null;
                    return (
                      <tr key={product.sku} className={deadSkus.includes(product.sku) ? 'dead-sku-row' : ''}>
                        <td>{product.sku}</td>
                        <td>{product.productName}</td>
                        {smoothedProduct.forecasts.map(month => {
                          const monthNum = month.period.split('-')[1];
                          const hasHistoricalData = historicalMonthlyAverages[product.sku] && historicalMonthlyAverages[product.sku][monthNum];
                          const historicalAvg = hasHistoricalData ? historicalMonthlyAverages[product.sku][monthNum].avgQty : 0;
                          const smoothedQty = month.salesQuantity;
                          const diff = hasHistoricalData ? smoothedQty - historicalAvg : 0;
                          const diffPercent = hasHistoricalData && historicalAvg > 0 ? ((smoothedQty - historicalAvg) / historicalAvg * 100) : 0;
                          
                          // Get historical data points for tooltip
                          const historicalDataPoints = salesData
                            .filter(item => item.sku === product.sku && item.period.split('-')[1] === monthNum)
                            .sort((a, b) => a.period.localeCompare(b.period));
                          
                            const smoothedBreakdown = (() => {
                              if (!historicalDataPoints || historicalDataPoints.length === 0) return '';
                              let breakdown = `The exponential smoothing formula is: F_{t+1} = α·A_t + (1-α)·F_t\n\nWhere:\n• F_{t+1} = Next period's forecast\n• A_t = Actual value at time t\n• F_t = Previous forecast\n• α = Smoothing constant (0.3)\n\nThe calculation process:\n1. Start with F_0 = A_0 (first actual value)\n\n2. For each subsequent period:\n   • Take 30% of the current actual value (α·A_t)\n   • Take 70% of the previous forecast ((1-α)·F_t)\n   • Add them together to get the new forecast\n\nExample calculation:\n`;
                              breakdown += historicalDataPoints.slice(0, 3).map((point, i) => {
                                if (i === 0) {
                                  return `Initial value: F_0 = A_0 = ${point.salesQuantity} units\n`;
                                }
                                const prevSmoothed = i === 1 ? historicalDataPoints[0].salesQuantity : 
                                  (0.3 * historicalDataPoints[i-1].salesQuantity + 0.7 * historicalDataPoints[0].salesQuantity);
                                const newForecast = 0.3 * point.salesQuantity + 0.7 * prevSmoothed;
                                return `Period ${i}:\n   • Actual value (A_${i}) = ${point.salesQuantity} units\n   • Previous forecast (F_${i-1}) = ${prevSmoothed.toFixed(2)} units\n   • New forecast (F_${i+1}) = 0.3 × ${point.salesQuantity} + 0.7 × ${prevSmoothed.toFixed(2)} = ${newForecast.toFixed(2)} units\n`;
                              }).join('\n');
                              breakdown += `\nThis method:\n• Gives more weight to recent data (30%)\n• Maintains some influence from older data (70%)\n• Smooths out random fluctuations\n• Adapts to trends while reducing noise`;
                              return breakdown;
                            })();

                          return [
                            <td key={month.period + '-qty'} className="forecast-cell">
                              <div className="forecast-value">{formatNumberWithCommas(Math.round(smoothedQty), 0)}</div>
                              {hasHistoricalData && (
                                <div className="historical-comparison comparison-light">
                                  {formatComparisonText(diff, diffPercent)}
                                  <InfoTooltip 
                                      text="Exponential Smoothing Forecast"
                                      breakdown={smoothedBreakdown}
                                  />
                                </div>
                              )}
                            </td>,
                            <td key={month.period + '-rev'} className="forecast-cell">
                              <div className="forecast-value">${formatNumberWithCommas(Math.round(month.salesRevenue), 2)}</div>
                            </td>
                          ];
                        })}
                        <td>{formatNumberWithCommas(Math.round(smoothedProduct.totalQuantity), 0)}</td>
                        <td>${formatNumberWithCommas(Math.round(smoothedProduct.totalRevenue), 2)}</td>
                        <td>
                          {productMonthlyAverages[product.sku] 
                            ? formatNumberWithCommas(Math.round(productMonthlyAverages[product.sku].avgQty), 0)
                            : 'N/A'}
                        </td>
                        <td>
                          ${productMonthlyAverages[product.sku] 
                            ? formatNumberWithCommas(Math.round(productMonthlyAverages[product.sku].avgRevenue), 2)
                            : 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            ) : (
              <ForecastGraph
                data={smoothedForecasts.reduce((acc, product) => {
                  product.forecasts.forEach((forecast, idx) => {
                    // Only include periods within the selected date range
                    const forecastDate = new Date(forecast.period + "-01");
                    if (startDate && forecastDate < startDate) return;
                    if (endDate) {
                      const endDatePlusMonth = new Date(endDate);
                      endDatePlusMonth.setMonth(endDatePlusMonth.getMonth() + 1);
                      if (forecastDate >= endDatePlusMonth) return;
                    }
                    if (!acc[idx]) {
                      acc[idx] = {
                        period: forecast.period,
                        totalQuantity: 0,
                        totalRevenue: 0
                      };
                    }
                    acc[idx].totalQuantity += forecast.salesQuantity;
                    acc[idx].totalRevenue += forecast.salesRevenue;
                  });
                  return acc;
                }, [] as { period: string; totalQuantity: number; totalRevenue: number }[])
                  // Filter out periods outside the date range
                  .filter(item => {
                    const itemDate = new Date(item.period + "-01");
                    if (startDate && itemDate < startDate) return false;
                    if (endDate) {
                      const endDatePlusMonth = new Date(endDate);
                      endDatePlusMonth.setMonth(endDatePlusMonth.getMonth() + 1);
                      if (itemDate >= endDatePlusMonth) return false;
                    }
                    return true;
                  })
                }
                title="Smoothed Forecast Overview"
              />
            )}
          </div>
        )}

        {activeTab === 'historical' && (
          <div className="historical-view">
            <div className="historical-summary">
              <h3>Historical Data Summary</h3>
              <div className="summary-cards">
                <div className="summary-card">
                  <h4>Total Products</h4>
                  <span>{new Set(filteredData.historical.map(item => item.sku)).size}</span>
                </div>
                <div className="summary-card">
                  <h4>Total Quantity</h4>
                  <span>{formatNumberWithCommas(Math.round(filteredData.historical.reduce((sum, item) => sum + item.salesQuantity, 0)), 0)}</span>
                </div>
                <div className="summary-card">
                  <h4>Total Revenue</h4>
                  <span>${formatNumberWithCommas(Math.round(filteredData.historical.reduce((sum, item) => sum + item.salesRevenue, 0)), 2)}</span>
                </div>
                <div className="summary-card">
                  <h4>Avg. Price</h4>
                  <span>
                    ${(filteredData.historical.reduce((sum, item) => sum + item.salesRevenue, 0) / 
                      filteredData.historical.reduce((sum, item) => sum + item.salesQuantity, 0) || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="view-toggle" style={{ display: 'flex', gap: '10px', marginBottom: '1rem' }}>
              <button
                className={`px-4 py-2 text-sm rounded border ${
                  historicViewType === 'table' 
                    ? 'bg-[#006d79] text-white' 
                    : 'border-[#006d79] text-[#006d79]'
                }`}
                onClick={() => setHistoricViewType('table')}
              >
                Table View
              </button>
              <button
                className={`px-4 py-2 text-sm rounded border ${
                  historicViewType === 'graph' 
                    ? 'bg-[#006d79] text-white' 
                    : 'border-[#006d79] text-[#006d79]'
                }`}
                onClick={() => setHistoricViewType('graph')}
              >
                Graph View
              </button>
            </div>
            {historicViewType === 'table' ? (
            <div className="table-container">
              <table className="forecast-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product Name</th>
                    {filteredData.historical
                      .reduce((periods: string[], item) => {
                        if (!periods.includes(item.period)) {
                          periods.push(item.period);
                        }
                        return periods;
                      }, [])
                      .sort()
                      .map(period => (
                        <th key={period} className="text-center">
                          {period}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.products.map(product => (
                    <tr key={product.sku} className={deadSkus.includes(product.sku) ? 'dead-sku-row' : ''}>
                      <td>{product.sku}</td>
                      <td>{product.productName}</td>
                      {filteredData.historical
                        .reduce((periods: string[], item) => {
                          if (!periods.includes(item.period)) {
                            periods.push(item.period);
                          }
                          return periods;
                        }, [])
                        .sort()
                        .map(period => {
                          const periodData = filteredData.historical.find(
                            item => item.sku === product.sku && item.period === period
                          );
                          return (
                            <td key={`${product.sku}-${period}`} className="forecast-cell">
                              <div className="forecast-value">
                                {periodData?.salesQuantity.toLocaleString() || 0}
                              </div>
                              <div className="text-xs text-gray-500">
                                ${periodData?.salesRevenue.toFixed(2) || '0.00'}
                              </div>
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            ) : (
              <ForecastGraph
                data={(() => {
                  // Aggregate by period for next 12 months, filtered by date range
                  const periodMap: { [period: string]: { totalQuantity: number; totalRevenue: number } } = {};
                  filteredData.historical.forEach(item => {
                    const itemDate = new Date(item.period + '-01');
                    if (startDate && itemDate < startDate) return;
                    if (endDate) {
                      const endDatePlusMonth = new Date(endDate);
                      endDatePlusMonth.setMonth(endDatePlusMonth.getMonth() + 1);
                      if (itemDate >= endDatePlusMonth) return;
                    }
                    if (!periodMap[item.period]) {
                      periodMap[item.period] = { totalQuantity: 0, totalRevenue: 0 };
                    }
                    periodMap[item.period].totalQuantity += item.salesQuantity;
                    periodMap[item.period].totalRevenue += item.salesRevenue;
                  });
                  return Object.entries(periodMap).map(([period, vals]) => ({
                    period,
                    totalQuantity: vals.totalQuantity,
                    totalRevenue: vals.totalRevenue
                  }));
                })()}
                title="Historic Sales Overview"
              />
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div className="products-view">
            <div className="view-controls" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Product Sales Trends</h3>
              <div className="view-toggle" style={{ display: 'flex', gap: '10px' }}>
                <button
                  className={`px-4 py-2 text-sm rounded border ${
                    productViewType === 'cards' 
                      ? 'btn-primary' 
                      : 'btn-secondary'
                  }`}
                  onClick={() => setProductViewType('cards')}
                >
                  Summary Cards
                </button>
                <button
                  className={`px-4 py-2 text-sm rounded border ${
                    productViewType === 'table' 
                      ? 'btn-primary' 
                      : 'btn-secondary'
                  }`}
                  onClick={() => setProductViewType('table')}
                >
                  Table Layout
                </button>
              </div>
            </div>

            {productViewType === 'cards' ? (
            <div className="product-analysis-chart">
              <MemoizedSalesChart 
                salesData={salesData} 
                forecasts={forecasts}
                searchTerm={searchTerm}
                filterType={filterType}
                excludeDeadSkus={excludeDeadSkus}
                excludeOutliers={excludeOutliers}
              />
            </div>
            ) : (
              <div className="table-container">
                <table className="historical-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Product Name</th>
                      {allPeriods.map(period => [
                        <th key={`${period}-qty`}>{period} Units</th>,
                        <th key={`${period}-rev`}>{period} Rev</th>
                      ])}
                      <th>Total Units</th>
                      <th>Total Revenue</th>
                      <th>Avg Units/Month</th>
                      <th>Avg Revenue/Month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.products.map(product => {
                      const periodData = getSalesDataBySkuPeriod(product.sku);
                      const totalQty = Object.values(periodData).reduce((sum, data) => sum + data.qty, 0);
                      const totalRev = Object.values(periodData).reduce((sum, data) => sum + data.revenue, 0);
                      const avgQty = totalQty / Object.keys(periodData).length;
                      const avgRev = totalRev / Object.keys(periodData).length;

                      return (
                        <tr key={product.sku} className={deadSkus.includes(product.sku) ? 'dead-sku-row' : ''}>
                          <td>{product.sku}</td>
                          <td>{product.productName}</td>
                          {allPeriods.map(period => {
                            const data = periodData[period] || { qty: 0, revenue: 0 };
                            return [
                              <td key={`${period}-qty`}>{formatNumberWithCommas(Math.round(data.qty), 0)}</td>,
                              <td key={`${period}-rev`}>${formatNumberWithCommas(Math.round(data.revenue), 2)}</td>
                            ];
                          })}
                          <td>{formatNumberWithCommas(Math.round(totalQty), 0)}</td>
                          <td>${formatNumberWithCommas(Math.round(totalRev), 2)}</td>
                          <td>{formatNumberWithCommas(Math.round(avgQty), 0)}</td>
                          <td>${formatNumberWithCommas(Math.round(avgRev), 2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {productViewType === 'cards' && filteredData.products.map(product => {
              // Historical data for this SKU
              const skuSales = salesData.filter(item => item.sku === product.sku);
              const totalUnits = skuSales.reduce((sum, item) => sum + item.salesQuantity, 0);
              const totalRevenue = skuSales.reduce((sum, item) => sum + item.salesRevenue, 0);
              const { min, max } = getDateRangeForSku(product.sku);
              const totalDays = min && max ? Math.max(1, Math.round((max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24)) + 1) : 1;
              const runRate = totalUnits / totalDays;
              const years = getYearsForSku(product.sku);
              const lastFullYear = years[years.length - 1];
              const prevFullYear = years[years.length - 2];
              const lastYearUnits = lastFullYear ? getTotalUnitsForSkuYear(product.sku, lastFullYear) : 0;
              const prevYearUnits = prevFullYear ? getTotalUnitsForSkuYear(product.sku, prevFullYear) : 0;
              const yoyGrowth = lastYearUnits - prevYearUnits;
              const yoyGrowthRate = prevYearUnits > 0 ? (yoyGrowth / prevYearUnits) * 100 : 0;
              const firstSale = skuSales.length > 0 ? skuSales.reduce((min, item) => item.period < min ? item.period : min, skuSales[0].period) : null;
              const lastSale = skuSales.length > 0 ? skuSales.reduce((max, item) => item.period > max ? item.period : max, skuSales[0].period) : null;
              // Group by month
              const monthGroups: { [month: string]: number } = {};
              skuSales.forEach(item => {
                monthGroups[item.period] = (monthGroups[item.period] || 0) + item.salesQuantity;
              });
              const monthEntries = Object.entries(monthGroups);
              const bestMonth = monthEntries.length > 0 ? monthEntries.reduce((best, curr) => curr[1] > best[1] ? curr : best, monthEntries[0]) : null;
              const worstMonth = monthEntries.filter(([_, qty]) => qty > 0).length > 0 ? monthEntries.filter(([_, qty]) => qty > 0).reduce((worst, curr) => curr[1] < worst[1] ? curr : worst, monthEntries.filter(([_, qty]) => qty > 0)[0]) : null;
              // Months with no sales
              const allMonths = allPeriods;
              const monthsWithNoSales = allMonths.filter(month => !monthGroups[month] || monthGroups[month] === 0).length;

              return (
              <div key={product.sku} className="sku-section">
                <div className="sku-header">
                  <h4>{product.sku}</h4>
                  <div className="product-name">{product.productName}</div>
                  <div className="product-summary">
                      <span><strong>Total Units Sold:</strong> {formatNumberWithCommas(totalUnits, 0)}</span>
                      <span> | <strong>Total Revenue:</strong> ${formatNumberWithCommas(totalRevenue, 2)}</span>
                      <span> | <strong>Monthly Avg:</strong> {productMonthlyAverages[product.sku] ? formatNumberWithCommas(Math.round(productMonthlyAverages[product.sku].avgQty), 0) : 'N/A'} units</span>
                  </div>
                    <div className="product-metrics" style={{ display: 'flex', gap: '18px', marginTop: 8, flexWrap: 'wrap' }}>
                      <span><strong>Avg Run Rate:</strong> {runRate.toFixed(2)} units/day</span>
                      <span><strong>YOY Growth:</strong> {formatNumberWithCommas(yoyGrowth, 0)} units</span>
                      <span><strong>YOY Growth Rate:</strong> {yoyGrowthRate.toFixed(1)}%</span>
                      <span><strong>First Sale:</strong> {firstSale || 'N/A'}</span>
                      <span><strong>Last Sale:</strong> {lastSale || 'N/A'}</span>
                      <span><strong>Best Month:</strong> {bestMonth ? `${bestMonth[0]} (${bestMonth[1]} units)` : 'N/A'}</span>
                      <span><strong>Worst Month:</strong> {worstMonth ? `${worstMonth[0]} (${worstMonth[1]} units)` : 'N/A'}</span>
                      <span><strong>Months w/ No Sales:</strong> {monthsWithNoSales}</span>
                </div>
                              </div>
                  {/* No forecast or future months here! */}
                        </div>
                      );
                    })}
          </div>
        )}
      </div>
    </div>
  );
}; 